import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import LazyImage from '../../../../ui-core/Image';
import {
  upperBack,
  shoulder,
  hips,
  ankles,
  upperBackWebp,
  shoulderWebp,
  hipsWebp,
  anklesWebp,
} from '../../images';
import './style.css';
import Button from '../../../../ui-core/Button/Button';
import { TEXT_CONSTANT } from '../../../../constants/app';
import {
  addAosAnimateClassObserver,
  formatCurrency,
} from '../../../../utils/helpers';

export const FoundationDetails = ({
  isLoading,
  onClick,
  courseDetail,
  currentPrice,
}) => {
  const elementRef = useRef();
  const upperBackRef = useRef();
  const shoulderRef = useRef();
  const hipRef = useRef();
  const anklesRef = useRef();
  useEffect(() => {
    const observer = addAosAnimateClassObserver();
    if (elementRef?.current) {
      observer.observe(elementRef.current);
    }
    if (upperBackRef?.current) {
      observer.observe(upperBackRef.current);
    }
    if (shoulderRef?.current) {
      observer.observe(shoulderRef.current);
    }
    if (hipRef?.current) {
      observer.observe(hipRef.current);
    }
    if (anklesRef?.current) {
      observer.observe(anklesRef.current);
    }

    return () => {
      elementRef?.current && observer.unobserve(elementRef?.current);
      upperBackRef?.current && observer.unobserve(upperBackRef?.current);
      shoulderRef?.current && observer.unobserve(shoulderRef?.current);
      hipRef?.current && observer.unobserve(hipRef?.current);
      anklesRef?.current && observer.unobserve(anklesRef?.current);
    };
  }, [
    elementRef?.current,
    upperBackRef?.current,
    shoulderRef?.current,
    hipRef?.current,
    anklesRef?.current,
  ]);
  const handleClick = () => {
    if (onClick) {
      onClick(TEXT_CONSTANT.JOIN_NOW, 'isSignup', 'You are not Alone');
    }
  };
  return (
    <div
      className="mobility-course"
      ref={elementRef}
      data-aos="fade-in"
      data-aos-delay="50"
      data-aos-duration="1000"
      data-aos-easing="ease-in-out"
    >
      <h2>
        You are not <em>Alone</em>
      </h2>
      <p>
        This course will focus on the 4 key areas of the body that need mobility
        the most:
      </p>

      <Row className="course">
        <Col lg="6" className="ps-lg-0">
          <h3>
            <span>Upper Back</span>
          </h3>
          <p>
            When you improve your upper back (or thoracic spine) mobility, you
            can help reduce:
          </p>
          <ul>
            <li>Neck Tension</li>
            <li>Chronic Headaches</li>
            <li>Forward Head Posture</li>
            <li>Difficulty Reaching Overhead</li>
            <li>Low Back Tension</li>
            <li>Rounded Shoulders</li>
          </ul>
        </Col>
        <Col
          ref={upperBackRef}
          data-aos="fade-left"
          data-aos-delay="50"
          data-aos-duration="1000"
          data-aos-easing="ease-in-out"
          lg="5"
          className="img position-relative offset-lg-1"
        >
          <LazyImage
            errorSrc={upperBack}
            placeholderSrc={upperBackWebp}
            src={upperBackWebp}
            className="w-100 img-fluid"
          />
          <div className="clr-bg"></div>
        </Col>
      </Row>

      <Row className="course">
        <Col
          lg="5"
          className="img left position-relative"
          ref={shoulderRef}
          data-aos="fade-right"
          data-aos-delay="50"
          data-aos-duration="1000"
          data-aos-easing="ease-in-out"
        >
          <LazyImage
            errorSrc={shoulder}
            placeholderSrc={shoulderWebp}
            src={shoulderWebp}
            className="w-100 img-fluid"
          />
          <div className="clr-bg"></div>
        </Col>
        <Col lg="6" className="offset-lg-1">
          <h3>
            <span>Shoulders</span>
          </h3>
          <p>When you improve shoulder mobility, you can help reduce:</p>
          <ul>
            <li>Biceps Tendinitis</li>
            <li>Rotator Cuff Pain</li>
            <li>Shoulder Impingement</li>
            <li>Elbow Pain</li>
            <li>Trouble Reaching Overhead</li>
          </ul>
        </Col>
      </Row>

      <Row className="course">
        <Col lg="6" className="ps-lg-0">
          <h3>
            <span>Hips</span>
          </h3>
          <p>When you improve hip mobility, you can help reduce:</p>
          <ul>
            <li>Low Back Pain & Spasm</li>
            <li>Hip Bursitis</li>
            <li>Hip Impingement</li>
            <li>Hip Flexor Tightness</li>
            <li>Hip Popping</li>
            <li>Knee Pain</li>
          </ul>
        </Col>
        <Col
          lg="5"
          className="img two position-relative offset-lg-1"
          ref={hipRef}
          data-aos="fade-left"
          data-aos-delay="50"
          data-aos-duration="1000"
          data-aos-easing="ease-in-out"
        >
          <LazyImage
            errorSrc={hips}
            placeholderSrc={hipsWebp}
            src={hipsWebp}
            className="w-100 img-fluid"
          />
          <div className="clr-bg"></div>
        </Col>
      </Row>

      <Row className="course border-0">
        <Col
          lg="5"
          className="img left two position-relative"
          ref={anklesRef}
          data-aos="fade-right"
          data-aos-delay="50"
          data-aos-duration="1000"
          data-aos-easing="ease-in-out"
        >
          <LazyImage
            errorSrc={ankles}
            placeholderSrc={anklesWebp}
            src={anklesWebp}
            className="w-100 img-fluid"
          />
          <div className="clr-bg"></div>
        </Col>
        <Col lg="6" className="offset-lg-1">
          <h3>
            <span>Ankles</span>
          </h3>
          <p>When you improve ankle mobility, you can help reduce:</p>
          <ul>
            <li>Plantar Fascia Pain</li>
            <li>Shin Splints</li>
            <li>Achilles Tendinitis</li>
            <li>Knee Pain</li>
            <li>Over-pronation</li>
          </ul>
        </Col>
      </Row>

      <Button
        type="button"
        isLoading={isLoading}
        disabled={isLoading}
        onClick={handleClick}
        class="site-btn-stroke mx-auto mt-4"
        text={`${TEXT_CONSTANT.JOIN_NOW} at ${formatCurrency(
          currentPrice,
          courseDetail?.attributes?.currency,
          2,
        )}`}
      />
    </div>
  );
};

FoundationDetails.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  courseDetail: PropTypes.object,
  currentPrice: PropTypes.number,
};
